/**
 * Mandanten-Dokumente: Der Betreiber (Operator/Platform-Admin/Super-Admin)
 * stellt Dokumente je Mandant bereit (AV-Vertrag, AGB, Verträge, Rechnungen);
 * Company-Admins sehen und laden die Dokumente ihres eigenen Mandanten.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetList, useNotify, Title } from 'react-admin';
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, IconButton,
    MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField,
    Typography, Autocomplete,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    CompanyDocument, DOCUMENT_APPLICATIONS, DOCUMENT_CATEGORIES,
    deleteCompanyDocument, downloadCompanyDocument, getTokenClaims,
    listCompanyDocuments, uploadCompanyDocument,
} from '../services/documentService';

const categoryLabel = (id: string) => DOCUMENT_CATEGORIES.find((c) => c.id === id)?.name ?? id;
const applicationLabel = (id: string | null) =>
    DOCUMENT_APPLICATIONS.find((a) => a.id === (id ?? ''))?.name ?? id ?? 'Allgemein';

const formatSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
};

const CompanyDocuments = () => {
    const notify = useNotify();
    const claims = useMemo(getTokenClaims, []);
    const isOperator = claims.roles.some((r) =>
        ['ROLE_OPERATOR', 'ROLE_PLATFORM_ADMIN', 'ROLE_SUPER_ADMIN'].includes(r));

    const [companyId, setCompanyId] = useState<number | null>(isOperator ? null : claims.companyId);
    const [documents, setDocuments] = useState<CompanyDocument[]>([]);
    const [loading, setLoading] = useState(false);

    // Upload-Formular (nur Betreiber)
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('sonstiges');
    const [application, setApplication] = useState('');
    const [uploading, setUploading] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);

    // Mandanten-Auswahl fuer den Betreiber
    const { data: companies } = useGetList(
        'companies',
        { pagination: { page: 1, perPage: 500 }, sort: { field: 'name', order: 'ASC' } },
        { enabled: isOperator },
    );

    const load = useCallback(async (id: number) => {
        setLoading(true);
        try {
            setDocuments(await listCompanyDocuments(id));
        } catch (e: any) {
            notify(e.message || 'Dokumente konnten nicht geladen werden', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        if (companyId) {
            load(companyId);
        }
    }, [companyId, load]);

    const handleUpload = async () => {
        const file = fileInput.current?.files?.[0];
        if (!companyId || !file) {
            notify('Bitte Mandant und Datei wählen', { type: 'warning' });
            return;
        }
        setUploading(true);
        try {
            await uploadCompanyDocument(companyId, file, title, category, application);
            notify('Dokument hochgeladen', { type: 'success' });
            setTitle('');
            if (fileInput.current) fileInput.current.value = '';
            await load(companyId);
        } catch (e: any) {
            notify(e.message || 'Upload fehlgeschlagen', { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (doc: CompanyDocument) => {
        try {
            await downloadCompanyDocument(doc);
        } catch (e: any) {
            notify(e.message || 'Download fehlgeschlagen', { type: 'error' });
        }
    };

    const handleDelete = async (doc: CompanyDocument) => {
        if (!window.confirm(`Dokument „${doc.title}" wirklich löschen?`)) return;
        try {
            await deleteCompanyDocument(doc.uuid);
            notify('Dokument gelöscht', { type: 'success' });
            if (companyId) await load(companyId);
        } catch (e: any) {
            notify(e.message || 'Löschen fehlgeschlagen', { type: 'error' });
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Title title="Dokumente" />
            <Card>
                <CardContent>
                    <Typography variant="h5" gutterBottom>Dokumente</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {isOperator
                            ? 'Vom Betreiber je Mandant bereitgestellte Dokumente (AV-Vertrag, AGB, Verträge, Rechnungen).'
                            : 'Vom Betreiber für Ihre Organisation bereitgestellte Dokumente.'}
                    </Typography>

                    {isOperator && (
                        <Autocomplete
                            sx={{ maxWidth: 400, mb: 2 }}
                            options={companies ?? []}
                            getOptionLabel={(c: any) => c.name ?? ''}
                            onChange={(_, company: any) => setCompanyId(company ? Number(company.id) : null)}
                            renderInput={(params) => <TextField {...params} label="Mandant wählen" size="small" />}
                        />
                    )}

                    {isOperator && companyId && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                            <input type="file" ref={fileInput} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                                style={{ maxWidth: 220 }} data-testid="document-file-input" />
                            <TextField label="Titel" size="small" value={title}
                                onChange={(e) => setTitle(e.target.value)} sx={{ width: 240 }} />
                            <TextField select label="Kategorie" size="small" value={category}
                                onChange={(e) => setCategory(e.target.value)} sx={{ width: 180 }}>
                                {DOCUMENT_CATEGORIES.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </TextField>
                            <TextField select label="Produkt" size="small" value={application}
                                onChange={(e) => setApplication(e.target.value)} sx={{ width: 180 }}>
                                {DOCUMENT_APPLICATIONS.map((a) => (
                                    <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                                ))}
                            </TextField>
                            <Button variant="contained" startIcon={<UploadFileIcon />}
                                onClick={handleUpload} disabled={uploading}>
                                {uploading ? 'Lädt hoch…' : 'Hochladen'}
                            </Button>
                        </Box>
                    )}

                    {loading ? (
                        <CircularProgress size={28} />
                    ) : companyId ? (
                        documents.length === 0 ? (
                            <Typography color="text.secondary">Keine Dokumente vorhanden.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Titel</TableCell>
                                        <TableCell>Kategorie</TableCell>
                                        <TableCell>Produkt</TableCell>
                                        <TableCell>Datei</TableCell>
                                        <TableCell>Größe</TableCell>
                                        <TableCell>Bereitgestellt am</TableCell>
                                        <TableCell align="right">Aktionen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {documents.map((doc) => (
                                        <TableRow key={doc.uuid}>
                                            <TableCell>{doc.title}</TableCell>
                                            <TableCell>
                                                <Chip size="small" variant="outlined" label={categoryLabel(doc.category)} />
                                            </TableCell>
                                            <TableCell>
                                                {doc.application
                                                    ? <Chip size="small" label={applicationLabel(doc.application)} />
                                                    : <Typography variant="body2" color="text.secondary">Allgemein</Typography>}
                                            </TableCell>
                                            <TableCell>{doc.fileName}</TableCell>
                                            <TableCell>{formatSize(doc.fileSize)}</TableCell>
                                            <TableCell>{new Date(doc.createdAt).toLocaleDateString('de-DE')}</TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" title="Herunterladen" onClick={() => handleDownload(doc)}>
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                                {isOperator && (
                                                    <IconButton size="small" title="Löschen" onClick={() => handleDelete(doc)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )
                    ) : (
                        isOperator && <Typography color="text.secondary">Bitte einen Mandanten wählen.</Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default CompanyDocuments;
